import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Skeleton,
  useTheme
} from '@mui/material';
import {
  TrendingUp,
  Business,
  CompareArrows,
  Cloud,
  CloudOff
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import logger from '../utils/logger';

const CommunityUsage = () => {
  const theme = useTheme();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/metrics`);
        if (!response.ok) {
          throw new Error('Failed to fetch metrics');
        }
        const data = await response.json();
        setMetrics(data);
      } catch (err) {
        logger.error('Error fetching metrics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    
    // Refresh metrics every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Main stat cards configuration
  const mainStats = [
    {
      title: 'Organisations',
      value: metrics?.organisations || 0,
      subtitle: 'Unique organisations using IntuneMermaid',
      icon: <Business />,
      color: theme.palette.primary.main,
      bgColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)'
    },
    {
      title: 'Diagrams Generated',
      value: metrics?.totalComparisons || 0,
      subtitle: 'Total diagrams created',
      icon: <CompareArrows />,
      color: theme.palette.secondary.main,
      bgColor: theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.1)' : 'rgba(156, 39, 176, 0.05)'
    }
  ];

  // Feature usage stats configuration
  const featureStats = [
    {
      title: 'Total Apps Processed',
      value: metrics?.featureUsage?.applications || 0,
      color: '#2196f3'
    },
    {
      title: 'Total Profiles Processed',
      value: metrics?.featureUsage?.profiles || 0,
      color: '#9c27b0'
    },
    {
      title: 'Resources Generated in Live Mode',
      value: metrics?.featureUsage?.liveMode || 0,
      color: '#4caf50',
      icon: <Cloud fontSize="small" />
    },
    {
      title: 'Resources Generated in Offline Mode',
      value: metrics?.featureUsage?.offlineMode || 0,
      color: '#ff9800',
      icon: <CloudOff fontSize="small" />
    }
  ];

  if (error) {
    return null; // Silently fail - metrics are optional
  }

  return (
    <Card 
      sx={{ 
        mb: 4,
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(45, 45, 45, 0.9) 100%)'
          : 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%)',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TrendingUp sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Usage metrics
          </Typography>
        </Box>

        {/* Main Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {mainStats.map((stat, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: stat.bgColor,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%'
                }}
              >
                {loading ? (
                  <>
                    <Skeleton variant="text" width={100} height={24} />
                    <Skeleton variant="text" width={80} height={48} />
                    <Skeleton variant="text" width={150} height={20} />
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ color: stat.color, mr: 1 }}>{stat.icon}</Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.title}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 700, 
                        color: stat.color,
                        mb: 0.5
                      }}
                    >
                      {formatNumber(stat.value)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.subtitle}
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Feature Usage Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Grid container spacing={2}>
            {featureStats.map((feature, index) => (
              <Grid item xs={6} sm={3} key={index}>
                {loading ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Skeleton variant="text" width={60} height={40} sx={{ mx: 'auto' }} />
                    <Skeleton variant="text" width={80} height={20} sx={{ mx: 'auto' }} />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5
                      }}
                    >
                      {formatNumber(feature.value)}
                      {feature.icon}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {feature.title}
                    </Typography>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Last Updated Footer */}
        {!loading && metrics?.lastUpdated && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {formatDate(metrics.lastUpdated)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CommunityUsage;
